import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, ChevronRight, ChevronLeft, Crown, Trophy, Shield, 
  Sparkles, Users, BarChart3, Gift, Star, Zap, PartyPopper
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";

interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  route?: string;
  highlight?: string;
  position?: "center" | "bottom" | "top";
}

const tourSteps: TourStep[] = [
  {
    id: "welcome",
    title: "Welcome to BailBuddies Plus+! ⭐",
    description: "You have full access to all Plus+ features. Let's take a quick tour to discover everything you can do!",
    icon: <Star className="w-8 h-8 text-primary" />,
    position: "center",
  },
  {
    id: "dashboard",
    title: "Your Command Center",
    description: "The dashboard shows your reliability stats, upcoming events, friend circles, and quick actions. Everything you need at a glance!",
    icon: <BarChart3 className="w-8 h-8 text-cyan-400" />,
    route: "/dashboard",
    position: "center",
  },
  {
    id: "solid-status",
    title: "Solid Status & Streaks",
    description: "Track your 'Solid' events where everyone shows up! Build streaks and unlock prestige tiers from 'On-Time Human' to 'Unflakeable'.",
    icon: <Shield className="w-8 h-8 text-emerald-400" />,
    route: "/dashboard",
    position: "center",
  },
  {
    id: "streak-protection",
    title: "Streak Protection Tokens 🛡️",
    description: "Plus+ members get monthly tokens to protect their streaks. Life happens - but your streak doesn't have to suffer!",
    icon: <Zap className="w-8 h-8 text-amber-400" />,
    route: "/dashboard",
    position: "center",
  },
  {
    id: "friend-circles",
    title: "Friend Circles",
    description: "Organize your friends into groups like 'Friday Night Crew' or 'Gym Buddies'. Track each circle's collective reliability!",
    icon: <Users className="w-8 h-8 text-purple-400" />,
    route: "/dashboard",
    position: "center",
  },
  {
    id: "reliability-challenge",
    title: "Circle Reliability Challenge",
    description: "Your friend circles compete for the highest collective reliability score. Which crew is the most dependable?",
    icon: <Trophy className="w-8 h-8 text-orange-400" />,
    route: "/dashboard",
    position: "center",
  },
  {
    id: "profile",
    title: "Your Profile & Badges",
    description: "View your personal reliability badges, flake personality, and shareable stats. Unlock achievements like 'Rock Solid Friend'!",
    icon: <Star className="w-8 h-8 text-pink-400" />,
    route: "/profile",
    position: "center",
  },
  {
    id: "contacts",
    title: "Contact Manager & Comparison",
    description: "See reliability sparklines for each friend, compare friends side-by-side, and track who's most likely to show up!",
    icon: <Users className="w-8 h-8 text-blue-400" />,
    route: "/contacts",
    position: "center",
  },
  {
    id: "reliability-report",
    title: "My Reliability Report 📊",
    description: "Plus+ feature! Your personal Reliability Report shows all your perfect events, history, and insights. Celebrate your reliability legacy!",
    icon: <Trophy className="w-8 h-8 text-yellow-400" />,
    route: "/my-solid",
    position: "center",
  },
  {
    id: "memory-vault",
    title: "Memory Vault 📸",
    description: "Plus+ feature! All your past plans are saved here. Relive the memories and see reaction counts!",
    icon: <Gift className="w-8 h-8 text-violet-400" />,
    route: "/memory-vault",
    position: "center",
  },
  {
    id: "fun-zone",
    title: "Fun Zone 🎮",
    description: "Generate hilarious AI excuses, play Bail Bingo, crack open Fortune Cookies, and compete on leaderboards!",
    icon: <PartyPopper className="w-8 h-8 text-pink-400" />,
    route: "/fun-zone",
    position: "center",
  },
  {
    id: "ad-free",
    title: "Ad-Free Experience ✨",
    description: "As a Plus+ member, you enjoy a completely ad-free experience. Clean, focused, distraction-free!",
    icon: <Sparkles className="w-8 h-8 text-emerald-400" />,
    position: "center",
  },
  {
    id: "complete",
    title: "You're All Set! 🎉",
    description: "Explore, have fun, and remember - real friends show up! Use the 'Reset Data' button anytime to refresh the demo.",
    icon: <Crown className="w-8 h-8 text-yellow-400" />,
    route: "/dashboard",
    position: "center",
  },
];

interface OnboardingTourProps {
  isOpen: boolean;
  onClose: () => void;
}

const OnboardingTour = ({ isOpen, onClose }: OnboardingTourProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const step = tourSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tourSteps.length - 1;

  useEffect(() => {
    if (isOpen && step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [currentStep, isOpen, step.route, navigate, location.pathname]);

  const handleNext = () => {
    if (isLastStep) {
      onClose();
      setCurrentStep(0);
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    onClose();
    setCurrentStep(0);
    navigate("/dashboard");
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        onClick={(e) => e.target === e.currentTarget && handleSkip()}
      >
        <motion.div
          key={step.id}
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: -20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-md bg-primary rounded-3xl shadow-2xl border border-border overflow-hidden"
        >
          {/* Decorative gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-pink-500/10 pointer-events-none" />
          
          {/* Progress bar */}
          <div className="h-1 bg-muted">
            <motion.div
              className="h-full bg-primary"
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {/* Close button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors z-10"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>

          {/* Content */}
          <div className="p-6 pt-8 relative">
            {/* Step counter */}
            <div className="text-xs text-muted-foreground mb-4 font-medium">
              Step {currentStep + 1} of {tourSteps.length}
            </div>

            {/* Icon */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", delay: 0.1 }}
              className="w-16 h-16 rounded-2xl bg-card flex items-center justify-center mb-4"
            >
              {step.icon}
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
              className="text-xl font-display font-bold text-foreground mb-3"
            >
              {step.title}
            </motion.h2>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="text-muted-foreground leading-relaxed mb-6"
            >
              {step.description}
            </motion.p>

            {/* Navigation */}
            <div className="flex items-center justify-between gap-3">
              <Button
                variant="ghost"
                onClick={handlePrev}
                disabled={isFirstStep}
                className="flex-1"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="text-muted-foreground"
              >
                Skip
              </Button>

              <Button
                onClick={handleNext}
                className="flex-1 bg-primary hover:from-purple-700 hover:to-pink-700 text-white"
              >
                {isLastStep ? "Finish" : "Next"}
                {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>
            </div>
          </div>

          {/* Decorative dots */}
          <div className="flex justify-center gap-1.5 pb-4">
            {tourSteps.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep
                    ? "bg-purple-500 w-6"
                    : index < currentStep
                    ? "bg-purple-500/50"
                    : "bg-muted-foreground/30"
                }`}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingTour;
